import React, { useState, useRef, useEffect, useContext } from 'react';
import { Button } from 'reactstrap';
import { doubleMetaphone } from 'double-metaphone';
import Modal from '../components/Modals/Modal';
import { fillActiveField } from '../EMR_Integration/react_webInjector.ts';
import { useSpring, animated } from '@react-spring/web';
import MicDropStatus from './MicDropStatus';
import { GlobalContext } from './GlobalContext';

const URI = 'wss://micdrop.comprehendhealth.ai';

const INSTRUCTIONS =
  'Instantly insert text into your EMR by placing your cursor, saying a keyword, and letting the Mic-Drop feature do the rest.';

/* Logic for identifying the closest match to a keyword in a transcript 
if the transcript were "gate training" and the keyword were "gait training" we should match
A more edge case would be if the transcript was "subject" and there is a keyword "subjective" we should match
doubleMetaphone is used to find phonetic matches
Levenshtein distance is used to find the closest match
*/
// Function to compute the Levenshtein distance
// Function to compute the Levenshtein distance
function levenshteinDistance(s1, s2) {
  if (s1.length > s2.length) {
    [s1, s2] = [s2, s1];
  }
  let distances = Array(s1.length + 1)
    .fill()
    .map((_, i) => i);
  for (let i2 = 0; i2 < s2.length; i2++) {
    let distances_ = [i2 + 1];
    for (let i1 = 0; i1 < s1.length; i1++) {
      if (s1[i1] === s2[i2]) {
        distances_.push(distances[i1]);
      } else {
        distances_.push(
          1 + Math.min(distances[i1], distances[i1 + 1], distances_[i1]),
        );
      }
    }
    distances = distances_;
  }
  return distances[distances.length - 1];
}

function digitMap(text) {
  /**
   * Turn Digits into their equivalent words
   */
  // Map for converting digits to words
  const numberWords = [
    'zero',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
  ];

  // Map for converting ordinals (1st -> first, 2nd -> second, etc.)
  const ordinalWords = {
    '1st': 'first',
    '2nd': 'second',
    '3rd': 'third',
    '4th': 'fourth',
    '5th': 'fifth',
    '6th': 'sixth',
    '7th': 'seventh',
    '8th': 'eighth',
    '9th': 'ninth',
    '10th': 'tenth',
  };

  // Convert ordinal abbreviations to full words
  text = text.replace(/\b(\d+)(st|nd|rd|th)\b/g, (match, number, suffix) => {
    return ordinalWords[match] || match; // Look up in the ordinalWords map
  });

  // Convert individual digits to words
  text = text.replace(/\b\d+\b/g, (match) => {
    const number = parseInt(match, 10);
    if (number <= 10) {
      return numberWords[number]; // Convert numbers up to 10
    } else {
      return match; // Keep numbers greater than 10 as-is (or expand based on needs)
    }
  });

  return text;
}

// Function to find the best match for a transcribed word with a threshold
export function findBestMatch(transcribedWord, targetKeywords) {
  /**
   * Find the best match for a transcribed word in a list of target keywords
   * @param {string} transcribedWord - The transcribed word to match
   * @param {string[]} targetKeywords - The list of target keywords to match against
   * @returns {string} The closest matching keyword from the target
   */

  // Create a mapping of normalized keywords to their original forms
  const keywordMap = targetKeywords.reduce((map, keyword) => {
    const normalizedKeyword = digitMap(keyword);
    map[normalizedKeyword] = keyword;
    return map;
  }, {});

  // Normalize the transcribed word
  const normalizedTranscribedWord = digitMap(transcribedWord);

  // Create a list of normalized target keywords for comparison
  const normalizedKeywords = Object.keys(keywordMap);

  // First try exact matching
  let exactMatch = normalizedKeywords.find((keyword) =>
    normalizedTranscribedWord.toLowerCase().includes(keyword.toLowerCase()),
  );
  if (exactMatch) {
    return keywordMap[exactMatch]; // Return the original keyword
  }

  // Then, try phonetic matching
  const [primaryTranscribed, secondaryTranscribed] = doubleMetaphone(
    normalizedTranscribedWord,
  );
  for (const normalizedKeyword of normalizedKeywords) {
    const originalKeyword = keywordMap[normalizedKeyword];
    const [primaryKeyword, secondaryKeyword] =
      doubleMetaphone(normalizedKeyword);

    // Fuzzy match based on phonetic similarity
    if (
      primaryTranscribed === primaryKeyword ||
      primaryTranscribed === secondaryKeyword ||
      secondaryTranscribed === primaryKeyword ||
      secondaryTranscribed === secondaryKeyword
    ) {
      return originalKeyword; // Return the original keyword
    }
  }

  // If no phonetic match, use Levenshtein distance of 30% of the keyword length
  let closestKeyword = null;
  let minDistance = Infinity;
  for (const normalizedKeyword of normalizedKeywords) {
    const originalKeyword = keywordMap[normalizedKeyword];
    const distance = levenshteinDistance(
      normalizedTranscribedWord,
      normalizedKeyword,
    );
    if (distance < minDistance) {
      closestKeyword = originalKeyword;
      minDistance = distance;
    }
  }

  // Set a threshold as a percentage of the keyword length (e.g., 30%)
  const threshold = Math.floor(closestKeyword.length * 0.3);

  return minDistance <= threshold ? closestKeyword : null;
}

const MicDrop = ({ questions }) => {
  const { apiClient } = useContext(GlobalContext);

  const [status, setStatus] = useState(
    'Attempting connection to MicDrop please wait...',
  );
  const [message, setMessage] = useState('None');
  const [isConnected, setIsConnected] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [recentMatch, setRecentMatch] = useState('None');
  const [questionsLocal, setQuestionsLocal] = useState(questions || {});
  const [tapCount, setTapCount] = useState(0);
  const [showMessages, setShowMessages] = useState(false);
  const [micDropDisabled, setMicDropDisabled] = useState(false);
  const [foundKeys, setFoundKeys] = useState([]); // Stores the keys we have found
  const [wordChangeCount, setWordChangeCount] = useState(0);
  var forgetAboutIt = '';

  useEffect(() => {
    setQuestionsLocal(questions || {});
    // check if any of the any of the questions have non empty strings
    setMicDropDisabled(!Object.values(questions || {}).some((value) => value));
  }, [questions]);

  const websocketRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);

  const SAMPLE_RATE = 16000;
  const BLOCKSIZE = 1024 * 2;

  const sendAudioToWebSocket = (websocket, audioData, status) => {
    let audioDataList = Array.from(audioData).map((sample) =>
      Math.round(sample * 32767),
    );
    let messageBody = {
      type: 'audio',
      data: audioDataList,
      status: String(status),
    };
    let sendMessage = {
      action: 'sendmessage',
      message: JSON.stringify(messageBody),
    };
    websocket.send(JSON.stringify(sendMessage));
  };

  const receiveFromWebSocket = (websocket) => {
    websocket.onmessage = (event) => {
      let message = JSON.parse(event.data);
      if (message.message === 'Forbidden') {
        // This seems to happen when the lambda function is not ready...TODO handle this better
        console.error('WebSocket connection forbidden');
      } else if (message.message === 'Ready!') {
        // This is the server saying it's ready to receive audio
        setStatus('MicDrop is ready!');
        console.log('MicDrop is ready!');
        setIsConnected(true);
      } else if (message.message !== 'Audio received') {
        console.log(`Message received from WebSocket: ${event.data}`);
        const messageData = JSON.parse(event.data);
        setMessage(event.data);
        processMessage(messageData.message);
        setWordChangeCount((prevCount) => prevCount + 1);
      }
    };
  };

  const processMessage = (transcript) => {
    // # This get's called as the utterance is built for examples it will give
    // # hello
    // # hello sir
    // # hello sire my
    // # hello sir, my name is
    // # hello sir, my name is jacob
    // # hello sir, my name is jacob
    // #
    // # So we have to add logic so we don't constantly print/paste the same thing if the keyword is in the utterance in each iteration

    // # Need to clear the forget about it when the utterance has been completed
    // # TODO look to see if this is the start of the utterance rather than this hacky logic
    if (forgetAboutIt && !transcript.includes(forgetAboutIt)) {
      // setForgetAboutIt('');
      forgetAboutIt = '';
    }

    // # Remove the keyword from the utterance as necessary
    if (transcript.startsWith(forgetAboutIt)) {
      transcript = transcript.replace(forgetAboutIt, '', 1);
    }

    // # Find the closest match to the keyword in the transcript
    let closestKeyword = findBestMatch(transcript, Object.keys(questionsLocal));
    if (closestKeyword) {
      console.log('Match found');
      setRecentMatch(closestKeyword);
      console.log('Filling active field with:', questionsLocal[closestKeyword]);
      fillActiveField(questionsLocal[closestKeyword]);

      // Use a functional update to ensure you get the latest foundKeys state
      setFoundKeys((prevFoundKeys) => {
        const updatedKeys = [...prevFoundKeys, closestKeyword];
        console.log('foundKeys:', updatedKeys);
        return updatedKeys;
      });

      forgetAboutIt = transcript;
    }
  };

  const audioStream = async (websocket) => {
    let sendCount = 0;

    audioContextRef.current = new (window.AudioContext ||
      window.webkitAudioContext)({ sampleRate: SAMPLE_RATE });
    processorRef.current = audioContextRef.current.createScriptProcessor(
      BLOCKSIZE,
      1,
      1,
    );

    processorRef.current.onaudioprocess = (event) => {
      sendCount++;
      let audioData = event.inputBuffer.getChannelData(0);
      sendAudioToWebSocket(websocket, audioData, sendCount);
      receiveFromWebSocket(websocket);
    };

    navigator.mediaDevices
      .getUserMedia({ audio: { sampleRate: SAMPLE_RATE, channelCount: 1 } })
      .then((stream) => {
        audioContextRef.current.streamBeingCaptured = stream;
        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(processorRef.current);
        processorRef.current.connect(audioContextRef.current.destination);
        console.log('Starting audio capture and sending to WebSocket');
        setStatus('Connected and streaming audio');
      })
      .catch((err) => {
        console.error('Error accessing audio stream:', err);
        setStatus('Error accessing audio stream');
      });
  };

  const startTranscribing = async () => {
    // if any of the sections are not comprehended yet call comprehend at them
    // TODO, wait to establish the websocket until all the sections are comprehended, state management is hard...maybe we just comprehend directly here?
    // let comprehends = [];
    // for (let keyword in questionsLocal) {
    //   if (!questionsLocal[keyword] || questionsLocal[keyword] === '') {
    //     // run comprehend on the keyword keeping track of all the promises in an array
    //     let comprehend = callComprehend(keyword);
    //     comprehends.push(comprehend);
    //   }
    // }
    // // wait for all the comprehends to finish
    // await Promise.all(comprehends);

    // Start MicDrop
    setModalOpen(true);
    websocketRef.current = new WebSocket(URI);
    websocketRef.current.onopen = async () => {
      console.log('Connected to WebSocket');
      // Time to authenticate
      setStatus('Authenticating WebSocket connection...');
      console.log('Authenticating WebSocket connection...');
      await authenticateWebsocket(websocketRef.current);

      // Start audio stream
      setStatus('Starting audio stream...');
      console.log('Starting audio stream...');
      await audioStream(websocketRef.current);

      // Wait for the lambda function to start
      setStatus('Waiting for Lambda function to start...');
      console.log('Waiting for Lambda function to start...');
      // The websocket message receiver is waiting for "Ready!" to be sent
      // readyConnectionFallback(); I don't think this is needed...the lambda function MUST send the ready message which it does once it's ready
    };
    websocketRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setStatus('WebSocket error');
    };
    websocketRef.current.onclose = () => {
      console.log('WebSocket connection closed.');
      setStatus('WebSocket connection closed');
    };
  };

  function authenticateWebsocket(websocket) {
    let messageBody = {
      type: 'init',
      sessionEmail: apiClient.sessionEmail,
      sessionKey: apiClient.sessionKey,
      keywords: Object.keys(questionsLocal),
    };
    let sendMessage = {
      action: 'sendmessage',
      message: JSON.stringify(messageBody),
    };
    return new Promise((resolve, reject) => {
      websocket.send(JSON.stringify(sendMessage));
      websocket.onmessage = (event) => {
        let message = JSON.parse(event.data);
        if (message.message === 'Success') {
          resolve();
        } else {
          reject(new Error('Authentication failed'));
        }
      };
    });
  }

  async function readyConnectionFallback() {
    await new Promise((resolve) => setTimeout(resolve, 15000));
    console.log('Fallback readyConnectionFallback');
    setStatus('MicDrop is ready!');
    console.log('MicDrop is ready!');
    setIsConnected(true);
  }

  const stopTranscribing = () => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (
      audioContextRef.current &&
      audioContextRef.current.createMediaStreamSource
    ) {
      audioContextRef.current.streamBeingCaptured
        .getTracks() //get all tracks from the stream
        .forEach((track) /*of type MediaStreamTrack*/ => track.stop()); //stop each one
    }

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    console.log('WebSocket connection closed and audio context stopped.');
    setStatus('WebSocket connection closed');
    setMessage('None');
    setFoundKeys([]);
    setRecentMatch('None');
    setIsConnected(false);
    setModalOpen(false);
  };

  const handleStatusClick = () => {
    setTapCount(tapCount + 1);
    if (tapCount + 1 === 5) {
      setShowMessages(!showMessages);
      setTapCount(0); // Reset tap count
    }
  };

  // Modal Close Button
  const closeButton = {
    label: `Close`,
    color: 'secondary',
    onClick: stopTranscribing,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={startTranscribing}
          disabled={micDropDisabled}
          style={{ width: '100%' }}
          color="primary"
        >
          Start MicDrop
        </Button>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={stopTranscribing}
        buttons={[closeButton]}
      >
        <h1>Micdrop Client</h1>
        <p>{INSTRUCTIONS}</p>

        <p
          onClick={handleStatusClick}
          style={{ cursor: 'pointer', marginBottom: '0px' }}
        >
          Status:&nbsp;&nbsp;&nbsp;
          {isModalOpen && (
            <MicDropStatus
              isConnected={isConnected}
              wordChangeCount={wordChangeCount}
            />
          )}
        </p>
        {/* Being extra verbose that we are working on connecting  */}
        {!isConnected && (
          <p style={{ marginTop: '-20px', color: 'red' }}>
            Please wait, Connecting...
          </p>
        )}

        {showMessages && (
          // this is the hidden streaming text
          <p>
            Most Recent Message: <span>{message}</span>
          </p>
        )}

        <h2>Available Keys:</h2>
        <div className="tags-container">
          {Object.keys(questionsLocal).map((key, index) => (
            <div
              key={index}
              className={`tag ${foundKeys.includes(key) ? 'selected' : ''}`}
            >
              {key}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default MicDrop;
